// libs
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormControl,
  Button,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Box,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// assets
import ConsultationCodeSelectField from '../ConsultationCodeSelectField/ConsultationCodeSelectField';
import { getDisplayCode } from '../utils';
import StepTemplate from 'FRONTEND/components/Calendar/AppointmentForm/StepTemplate';
import {
  updateHasPayment,
  updatePayLater,
  pickDesiredConsultationCode,
} from 'FRONTEND/reducers/bookingSlice';
import { selectedExpertHasStripe } from 'FRONTEND/selectors/expertSelectors';
import { useGetConsultationCodesForUserQuery } from 'FRONTEND/utils/backendApi/consultationCodesApi';
import i18n from 'FRONTEND/utils/i18n';

export const PaymentToggleStep = ({
  isCompleted,
  isLocked = false,
  onStepClick = () => {},
  shouldReset = false,
  onStepCompleted = () => {},
  isAfterExpertStep = false,
  ...otherProps
}) => {
  const dispatch = useDispatch();
  const selectedExpertId = useSelector((state) => state.booking.selectedExpertId);
  const desiredConsultationCode = useSelector((state) => state.booking.desiredConsultationCode);
  const selectedExpertHasStripeConnected = useSelector(selectedExpertHasStripe);
  const expertHasStripe = selectedExpertHasStripeConnected && isAfterExpertStep;

  // hasPayment toggle state
  const initialHasPaymentState = useSelector((state) => state.booking.hasPayment);
  const [hasPaymentActive, setHasPaymentActive] = useState(Boolean(initialHasPaymentState)); // Payment active state
  const handleToggleHasPaymentCheckbox = (event) => {
    setHasPaymentActive(event.target.checked);
  };
  // isPayLater toggle state
  const initialPayLaterState = useSelector((state) => state.booking.isPayLater);
  const [isPayLaterActive, setIsPayLaterActive] = useState(initialPayLaterState); // 'pay later' active state
  const handleTogglePayLaterToggle = (newValue) => {
    if (newValue !== null) {
      setIsPayLaterActive(newValue);
      if (newValue) {
        dispatch(pickDesiredConsultationCode({ code: null })); // We send code = null when pay later is active
      }
    }
  };

  const autoCompleteWithNoPayment = () => {
    setHasPaymentActive(false);
    setIsPayLaterActive(false);
    dispatch(updateHasPayment({ hasPayment: false }));
    dispatch(pickDesiredConsultationCode({ code: null }));
    onStepCompleted();
  };

  const { isLoading, isError } = useGetConsultationCodesForUserQuery(selectedExpertId, { skip: !selectedExpertId || !isAfterExpertStep });


  useEffect(() => {
    if (isAfterExpertStep && !expertHasStripe && selectedExpertId) {
      autoCompleteWithNoPayment();
    }
  }, [isAfterExpertStep, expertHasStripe, selectedExpertId]);

  useEffect(() => {
    if (shouldReset) {
      dispatch(updatePayLater({ isPayLater: false }));
      dispatch(updateHasPayment({ hasPayment: null }));
      dispatch(pickDesiredConsultationCode({ code: null }));
    }
  }, [shouldReset]);

  const handleSubmit = () => {
    dispatch(updateHasPayment({ hasPayment: hasPaymentActive }));
    dispatch(updatePayLater({ isPayLater: isPayLaterActive }));
    onStepCompleted();
  };

  const locale = useSelector((state) => state.user.locale);

  const editText = hasPaymentActive ? (
    desiredConsultationCode && desiredConsultationCode.activeFeeAmount && isAfterExpertStep ? (
      getDisplayCode(desiredConsultationCode, locale)
    ) : (
      `${i18n._('Yes')}${isPayLaterActive && isAfterExpertStep ? ` (${i18n._('Pay later')})` : ''}`
    )
  ) : (
    i18n._('No')
  );

  return (
    <StepTemplate
      label={i18n._('Payment')}
      isLocked={isLocked}
      onEditClick={() => onStepClick()}
      isStepCompleted={isCompleted}
      editText={editText}
      {...otherProps}
    >
      <FormGroup sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <FormControl component='fieldset' variant='standard'>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasPaymentActive}
                  onChange={handleToggleHasPaymentCheckbox}
                  disabled={isAfterExpertStep && !expertHasStripe}
                  color='primary'
                />
              }
              label={i18n._('Activate')}
              labelPlacement='end'
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: '14px',
                },
              }}
            />
          </FormGroup>
        </FormControl>
        {!isLoading && hasPaymentActive && isAfterExpertStep && !isError && (
          <FormControl component='fieldset' variant='standard'>
            <FormGroup>
              {/* ToggleButtonGroup styling will be controlled by the theme on the frontend_library. */}
              <ToggleButtonGroup
                value={isPayLaterActive}
                exclusive
                onChange={(event, newValue) => handleTogglePayLaterToggle(newValue)}
                size='small'
              >
                <ToggleButton value={false}>
                  <Typography variant='body2'>{i18n._('Bill now')}</Typography>
                </ToggleButton>
                <ToggleButton value>
                  <Typography variant='body2'>{i18n._('Bill later')}</Typography>
                </ToggleButton>
              </ToggleButtonGroup>
            </FormGroup>
          </FormControl>
        )}
        {!isPayLaterActive && hasPaymentActive && isAfterExpertStep && (
          <Box sx={{ py: 1 }}>
            <ConsultationCodeSelectField />
          </Box>
        )}
      </FormGroup>
      <Button
        variant='contained'
        color='primary'
        sx={{ mt: 1, px: 8, py: 1 }}
        disabled={hasPaymentActive && isError && selectedExpertId}
        onClick={handleSubmit}
      >
        {i18n._('Validate')}
      </Button>
    </StepTemplate>
  );
};
